import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  FiGrid, FiPackage, FiShoppingBag, FiUsers, FiSettings,
  FiDollarSign, FiBox, FiClipboard, FiUpload, FiTruck,
  FiHeadphones, FiLink, FiShield, FiBarChart2, FiMessageCircle,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const ROLE_LINKS = {
  merchant: {
    title: 'Merchant Panel',
    links: [
      { to: '/merchant/dashboard', label: 'Dashboard', icon: FiGrid },
      { to: '/merchant/products', label: 'Products', icon: FiBox },
      { to: '/merchant/orders', label: 'Orders', icon: FiPackage },
      { to: '/merchant/coupons', label: 'Coupons', icon: FiDollarSign },
      { to: '/merchant/bulk-import', label: 'Bulk Import', icon: FiUpload },
      { to: '/merchant/store-settings', label: 'Store Settings', icon: FiSettings },
      { to: '/merchant/verification', label: 'Verification', icon: FiShield },
    ],
  },
  merchant_staff: {
    title: 'Staff Panel',
    links: [
      { to: '/merchant/dashboard', label: 'Dashboard', icon: FiGrid },
      { to: '/merchant/products', label: 'Products', icon: FiBox },
      { to: '/merchant/orders', label: 'Orders', icon: FiPackage },
    ],
  },
  admin: {
    title: 'Admin Panel',
    links: [
      { to: '/admin/dashboard', label: 'Dashboard', icon: FiGrid },
      { to: '/admin/orders', label: 'Orders', icon: FiClipboard },
      { to: '/admin/users', label: 'Users', icon: FiUsers },
      { to: '/admin/stores', label: 'Stores', icon: FiShoppingBag },
      { to: '/admin/revenue', label: 'Revenue', icon: FiDollarSign },
    ],
  },
  super_admin: {
    title: 'Super Admin',
    links: [
      { to: '/super-admin/dashboard', label: 'Dashboard', icon: FiShield },
      { to: '/super-admin/role-reference', label: 'Role Reference', icon: FiClipboard },
      { to: '/super-admin/feature-tracker', label: 'Feature Tracker', icon: FiBarChart2 },
      { to: '/admin/dashboard', label: 'Admin Panel', icon: FiGrid },
      { to: '/admin/users', label: 'Users', icon: FiUsers },
      { to: '/admin/stores', label: 'Stores', icon: FiShoppingBag },
      { to: '/admin/revenue', label: 'Revenue', icon: FiDollarSign },
    ],
  },
  delivery: {
    title: 'Delivery Panel',
    links: [
      { to: '/delivery/dashboard', label: 'Dashboard', icon: FiTruck },
    ],
  },
  support: {
    title: 'Support Panel',
    links: [
      { to: '/support/dashboard', label: 'Dashboard', icon: FiHeadphones },
    ],
  },
  affiliate: {
    title: 'Affiliate Panel',
    links: [
      { to: '/affiliate/dashboard', label: 'Dashboard', icon: FiLink },
    ],
  },
  warehouse: {
    title: 'Warehouse Panel',
    links: [
      { to: '/warehouse/dashboard', label: 'Dashboard', icon: FiBox },
    ],
  },
  moderator: {
    title: 'Moderator Panel',
    links: [
      { to: '/moderator/dashboard', label: 'Dashboard', icon: FiShield },
    ],
  },
  finance: {
    title: 'Finance Panel',
    links: [
      { to: '/finance/dashboard', label: 'Dashboard', icon: FiBarChart2 },
    ],
  },
  returns_manager: {
    title: 'Returns Panel',
    links: [
      { to: '/returns-manager/dashboard', label: 'Dashboard', icon: FiGrid },
    ],
  },
  marketing: {
    title: 'Marketing Panel',
    links: [
      { to: '/marketing/dashboard', label: 'Dashboard', icon: FiGrid },
    ],
  },
  catalog_manager: {
    title: 'Catalog Panel',
    links: [
      { to: '/catalog/dashboard', label: 'Dashboard', icon: FiGrid },
    ],
  },
  vendor_onboarding: {
    title: 'Onboarding Panel',
    links: [
      { to: '/vendor-onboarding/dashboard', label: 'Dashboard', icon: FiGrid },
    ],
  },
  ad_manager: {
    title: 'Ads Panel',
    links: [
      { to: '/ad-manager/dashboard', label: 'Dashboard', icon: FiGrid },
    ],
  },
  api_partner: {
    title: 'API Panel',
    links: [
      { to: '/api-partner/dashboard', label: 'Dashboard', icon: FiGrid },
    ],
  },
  community_manager: {
    title: 'Community Panel',
    links: [
      { to: '/community/dashboard', label: 'Dashboard', icon: FiGrid },
    ],
  },
  dispute_arbitrator: {
    title: 'Disputes Panel',
    links: [
      { to: '/disputes/dashboard', label: 'Dashboard', icon: FiGrid },
    ],
  },
  qa_tester: {
    title: 'QA Panel',
    links: [
      { to: '/qa-tester/dashboard', label: 'Dashboard', icon: FiGrid },
    ],
  },
  regional_manager: {
    title: 'Regional Panel',
    links: [
      { to: '/regional/dashboard', label: 'Dashboard', icon: FiGrid },
    ],
  },
};

const DashboardLayout = ({ children }) => {
  const { user } = useAuth();

  const config = ROLE_LINKS[user?.role] || ROLE_LINKS.merchant;
  const { title, links } = config;

  const roleBgClass = `role-bg-${user?.role || 'customer'}`;

  return (
    <div className={`section-padding ${roleBgClass}`} style={{ minHeight: 'calc(100vh - 72px)' }}>
      <div className="container-fluid">
        <div className="row g-4">
          {/* Sidebar */}
          <div className="col-lg-2 col-md-3">
            <div className="dashboard-card p-2">
              <h6 className="px-3 py-2 text-uppercase text-muted small fw-bold mb-0">{title}</h6>
              <nav className="d-flex flex-column">
                {links.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      `d-flex align-items-center gap-2 px-3 py-2 rounded text-decoration-none ${
                        isActive ? 'bg-primary bg-opacity-10 text-primary fw-semibold' : 'text-dark'
                      }`
                    }
                  >
                    <Icon size={16} />
                    <span className="small">{label}</span>
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>

          {/* Main content */}
          <div className="col-lg-10 col-md-9">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
